import { _ } from "i18n";
import type { ChangeEvent } from "react";
import "../Datagrid/pageControls.scss";

export type PageSizeOption = number | "all";

export const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [5, 10, 20, 30, 50, 100, "all"];

export type PaginationBarProps = {
	totalPages: number;
	currentPage: number;
	onPageChange: (nextPage: number) => void;
	showPageSizeSelector?: boolean;
	pageSize?: number;
	onPageSizeChange?: (nextPageSize: number) => void;
	pageSizeOptions?: PageSizeOption[];
	totalItems?: number;
	isError?: boolean;
	onRetry?: () => void;
};

export function PaginationBar({
	totalPages,
	currentPage,
	onPageChange,
	showPageSizeSelector = false,
	pageSize,
	onPageSizeChange,
	pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
	totalItems,
	isError = false,
	onRetry,
}: PaginationBarProps) {
	const safeTotalPages = Math.max(0, totalPages);
	const safeCurrentPage = Math.min(Math.max(1, currentPage), Math.max(1, safeTotalPages || 1));

	const canPrevious = safeTotalPages > 0 && safeCurrentPage > 1;
	const canNext = safeTotalPages > 0 && safeCurrentPage < safeTotalPages;

	const handleInputPageChange = (newPage: number) => {
		if (newPage > 0 && newPage <= safeTotalPages) {
			setTimeout(() => {
				onPageChange(newPage);
			}, 500);
		}
	};

	const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
		if (!onPageSizeChange) {
			return;
		}
		const value = e.target.value;
		const newSize = value === "all" ? (totalItems ?? 10000) : Number(value);
		onPageSizeChange(newSize);
	};

	const displayPageCount = safeTotalPages > 0 ? safeTotalPages : 1;

	return (
		<div className="tablePageControls-wrapper">
			<div className="tablePageControls-container">
				<>
					<div
						className="tablePageControls-goToStartEnd"
						onClick={() => {
							if (canPrevious) {
								onPageChange(1);
							}
						}}
					>
						<span className={`icon-new icon-chevron-left-grey ${canPrevious ? "icon-blue" : ""} icon-24`} />
						<span className={`icon-new icon-chevron-left-grey ${canPrevious ? "icon-blue" : ""} icon-24`} />
					</div>
					<div
						className="tablePageControls-prevNext"
						onClick={() => {
							if (canPrevious) {
								onPageChange(safeCurrentPage - 1);
							}
						}}
					>
						<span className={`icon-new icon-chevron-left-grey ${canPrevious ? "icon-blue" : ""} icon-24`} />
					</div>

					<div
						className="tablePageControls-prevNext"
						onClick={() => {
							if (canNext) {
								onPageChange(safeCurrentPage + 1);
							}
						}}
					>
						<span className={`icon-new icon-chevron-right-grey ${canNext ? "icon-blue" : ""} icon-24`} />
					</div>
					<div
						className="tablePageControls-goToStartEnd"
						onClick={() => {
							if (canNext) {
								onPageChange(safeTotalPages);
							}
						}}
					>
						<span className={`icon-new icon-chevron-right-grey ${canNext ? "icon-blue" : ""} icon-24`} />
						<span className={`icon-new icon-chevron-right-grey ${canNext ? "icon-blue" : ""} icon-24`} />
					</div>
					<div className="tablePageControls-count">{`${_("label.pagination.page")} ${safeCurrentPage} ${_(
						"label.pagination.of",
					)} ${displayPageCount} | ${_("label.pagination.go.to.page")}`}</div>
					<div>
						<input
							key={safeCurrentPage}
							className="tablePageControls-input"
							defaultValue={safeCurrentPage}
							type="number"
							min={1}
							max={Math.max(1, safeTotalPages)}
							onChange={(e) => handleInputPageChange(Number(e.target.value))}
						/>
					</div>
				</>
				{showPageSizeSelector && pageSize !== undefined && onPageSizeChange ? (
					<div className="tablePageControls-pageSizeSelector">
						<label htmlFor="paginationBar-pageSize">{_("label.pagination.rows_per_page") || "Rows per page"}:</label>
						<select id="paginationBar-pageSize" value={pageSize} onChange={handlePageSizeChange}>
							{pageSizeOptions.map((option) => (
								<option key={String(option)} value={option === "all" ? totalItems ?? 10000 : option}>
									{option === "all" ? _("label.pagination.all") || "All" : option}
								</option>
							))}
						</select>
					</div>
				) : null}
				{isError && onRetry ? (
					<div className="tablePageControls-retry">
						<span className="tablePageControls-retryMessage">{"Failed to load."}</span>
						<button type="button" className="tablePageControls-retryBtn" onClick={onRetry}>
							{_("label.retry") || "Retry"}
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
}
